import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { UploadCloud, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

/**
 * Sprint-2 DREAM-19 drag-drop file picker.
 *
 * Client-side type + size validation; rejections surface as a sonner toast
 * (per humanise-everywhere / toast-everywhere Sprint-1 rules). On accept the
 * parent's `onSelect(file)` runs; the parent owns the actual upload mutation.
 *
 * Reusable for eISF (Epic-6) and any future single-file upload surface.
 */

export interface DragDropUploadProps {
  /** Accept attribute, e.g. "application/pdf, .docx" — same as <input accept>. */
  accept: string;
  /** Max bytes; default 50 MB to match DREAM-19 AC3. */
  maxBytes?: number;
  /** Human-friendly description rendered under the icon (e.g. "PDF or DOCX, max 50 MB"). */
  acceptHint: string;
  /** Disable the control (e.g. while a previous upload is in flight). */
  disabled?: boolean;
  /** Called when the user picks/drops a valid file. */
  onSelect: (file: File) => void;
  /** If provided, render this filename inside the dropzone instead of the picker prompt. */
  currentFileName?: string | null;
  /** Click handler when the user wants to clear the current file. */
  onClear?: () => void;
}

const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;

export function DragDropUpload({
  accept,
  maxBytes = DEFAULT_MAX_BYTES,
  acceptHint,
  disabled = false,
  onSelect,
  currentFileName,
  onClear,
}: DragDropUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function validateAndSelect(file: File) {
    // Type check via the accept attribute. accept is a comma-separated list of
    // MIME types or extensions; we accept the file if any token matches.
    const tokens = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
    const fileExt = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
    const okType = tokens.length === 0 || tokens.some((t) =>
      t.startsWith('.') ? t === fileExt : file.type.toLowerCase() === t
    );
    if (!okType) {
      toast.error(`Only ${acceptHint.split(',')[0] ?? 'these formats'} accepted (got ${file.name}).`);
      return;
    }
    if (file.size === 0) {
      toast.error('Cannot upload an empty file.');
      return;
    }
    if (file.size > maxBytes) {
      const maxMb = Math.round(maxBytes / (1024 * 1024));
      toast.error(`File exceeds the ${maxMb} MB limit.`);
      return;
    }
    onSelect(file);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSelect(file);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    e.target.value = ''; // reset so re-selecting the same file fires again
  }

  if (currentFileName) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-surface-border bg-surface-muted/40 px-3 py-2.5 text-sm">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink-primary">{currentFileName}</div>
        </div>
        {onClear && !disabled && (
          <button
            type="button"
            className="text-ink-muted hover:text-danger-700"
            onClick={onClear}
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      aria-disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors',
        disabled ? 'cursor-not-allowed opacity-50 border-surface-border bg-surface-muted/30'
                 : 'cursor-pointer border-surface-border hover:border-accent-400 hover:bg-accent-50/40',
        dragOver && !disabled && 'border-accent-500 bg-accent-50',
      )}
    >
      <UploadCloud className="h-7 w-7 text-ink-muted" />
      <div className="text-sm font-medium text-ink-primary">
        Drag a file here or <span className="text-accent-700 underline">browse</span>
      </div>
      <div className="text-xs text-ink-muted">{acceptHint}</div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={handleInputChange}
        disabled={disabled}
      />
    </div>
  );
}
